"use strict";
import { AggregatorLeaderboard } from "pages/Swap/SwapSidePanel/AggregatorLeaderboard";
import { MainnetOverview } from "pages/Swap/SwapSidePanel/MainnetOverview";
import { memo, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { ExploreContextProvider } from "state/explore";
import { Flex, Text, styled } from "ui/src";
import { UniverseChainId } from "uniswap/src/features/chains/types";
import { useSwapFormContext } from "uniswap/src/features/transactions/swap/contexts/SwapFormContext";
import { CurrencyField } from "uniswap/src/types/currency";
const RING_STORIES = [
  {
    icon: "\u{1F6E1}\uFE0F",
    text: "Ring \u5DF2\u7ECF 6 \u5C81\u4E86\uFF0C\u4ECE\u672A\u51FA\u73B0\u8FC7\u8D44\u4EA7\u5B89\u5168\u95EE\u9898",
    textEn: "Ring is 6 years old and has never had a security incident"
  },
  {
    icon: "\u{1F4CA}",
    text: "Ring \u5728\u591A\u4E2A\u94FE\u7D2F\u8BA1\u4E3A\u5168\u7F51\u63D0\u4F9B\u8D85\u8FC7 2 \u5343\u4EBF\u4EA4\u6613\u91CF",
    textEn: "Ring has processed over $200B in cumulative volume across multiple chains"
  },
  {
    icon: "\u26A0\uFE0F",
    text: "Price Impact \u8D85\u8FC7\u6ED1\u70B9\u4F1A\u6709\u63D0\u9192\uFF0C10% \u4F1A\u963B\u6B62\u4EA4\u6613",
    textEn: "Warnings appear when price impact exceeds slippage; 10% blocks the trade"
  },
  { icon: "\u{1F465}", text: "\u7C7B\u4F3C\u670D\u52A1\u8D85\u8FC7 20 \u4E07+ \u94B1\u5305", textEn: "Similar services used by 200K+ wallets" },
  { icon: "\u{1F193}", text: "Ring \u4E0D\u4F1A\u6536\u53D6\u670D\u52A1\u8D39/\u5E73\u53F0\u8D39", textEn: "Ring does not charge any service or platform fees" },
  {
    icon: "\u{1F4B0}",
    text: "Web \u7528\u6237\u7684\u4EA4\u6613\u5982\u679C\u7ECF\u8FC7 Ring dex\uFF0C\u534F\u8BAE\u8D39 0.3% \u5C06\u4F1A\u8FD4\u8FD8",
    textEn: "Web trades routed through Ring DEX get a 0.3% protocol fee rebate"
  }
];
const ModalOverlay = styled(Flex, {
  zIndex: 1e3,
  alignItems: "center",
  justifyContent: "center",
  className: "ring-modal-overlay"
});
const ModalContent = styled(Flex, {
  backgroundColor: "$surface1",
  borderRadius: "$rounded20",
  borderWidth: "$spacing1",
  borderColor: "$surface3",
  p: "$spacing20",
  gap: "$spacing12",
  maxWidth: 440,
  width: "90%",
  maxHeight: "80vh",
  className: "ring-modal-content"
});
const PanelWrapper = styled(Flex, {
  width: "100%",
  flex: 1,
  minWidth: 320,
  maxWidth: 480,
  gap: "$spacing12"
});
const TabBadge = styled(Flex, {
  row: true,
  alignItems: "center",
  gap: "$spacing4",
  px: "$spacing16",
  py: "$spacing12",
  borderRadius: "$roundedFull",
  cursor: "pointer",
  borderWidth: "$spacing1",
  transition: "all 0.15s ease",
  variants: {
    active: {
      true: {
        backgroundColor: "$accent2",
        borderColor: "$accent1"
      },
      false: {
        backgroundColor: "$surface2",
        borderColor: "$surface3",
        hoverStyle: {
          borderColor: "$surface3Hovered",
          backgroundColor: "$surface2Hovered"
        }
      }
    }
  }
});
export const SwapSidePanel = memo(function SwapSidePanel2() {
  const { i18n } = useTranslation();
  const isZh = i18n.language?.startsWith("zh");
  const swapFormContext = useSwapFormContext();
  const exactAmountToken = swapFormContext?.exactAmountToken;
  const inputAmount = swapFormContext?.derivedSwapInfo?.currencyAmounts?.[CurrencyField.INPUT];
  const hasAmountIn = useMemo(() => {
    if (exactAmountToken && parseFloat(exactAmountToken) > 0) {
      return true;
    }
    if (inputAmount && !inputAmount.equalTo(0)) {
      return true;
    }
    return false;
  }, [exactAmountToken, inputAmount]);
  const [activeTab, setActiveTab] = useState(hasAmountIn ? "aggregator" : "ring");
  const [showStories, setShowStories] = useState(false);
  return <ExploreContextProvider chainId={UniverseChainId.Mainnet}><style>{`
        .ring-modal-overlay {
          position: fixed !important;
          top: 0 !important;
          left: 0 !important;
          right: 0 !important;
          bottom: 0 !important;
          width: 100vw !important;
          height: 100vh !important;
          background: rgba(0,0,0,0.5);
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;
          cursor: pointer;
        }
        .ring-modal-content {
          cursor: default;
          overflow-y: auto;
        }
        .ring-shimmer-text {
          background: linear-gradient(
            115deg,
            #888FAB 0%,
            #888FAB 35%,
            #FC74FE 46%,
            #FFFFFF 50%,
            #FC74FE 54%,
            #888FAB 65%,
            #888FAB 100%
          );
          background-size: 300% 100%;
          background-position: 100% 0;
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          animation: ringShimmer 6s ease-in-out infinite;
          display: inline-block;
        }
        @keyframes ringShimmer {
          0% {
            background-position: 100% 0;
          }
          35% {
            background-position: 0% 0;
          }
          100% {
            background-position: 0% 0;
          }
        }
      `}</style><PanelWrapper>{
    /* Tab switcher — hidden for now */
  }<Flex gap="$spacing8" alignItems="center"><Flex
    display="none"
    row
    alignItems="center"
    justifyContent="center"
    gap="$spacing8"
    flexWrap="wrap"
    width="100%"
  ><Flex row gap="$spacing8" flexWrap="wrap" justifyContent="center"><TabBadge active={activeTab === "ring"} onPress={() => setActiveTab("ring")}><Text variant="body4" color={activeTab === "ring" ? "$accent1" : "$neutral2"} fontWeight="600">
                  Ring Protocol
                </Text></TabBadge><TabBadge active={activeTab === "aggregator"} onPress={() => setActiveTab("aggregator")}><Text variant="body4" color={activeTab === "aggregator" ? "$accent1" : "$neutral2"} fontWeight="600">
                  Multi-DEX Aggregator
                </Text></TabBadge></Flex></Flex>{activeTab === "ring" && <Flex row alignItems="center" justifyContent="center" width="100%" gap="$spacing4" py="$spacing4"><Text variant="body2" fontWeight="600" textAlign="center" className="ring-shimmer-text">{isZh ? "Defi \u4E13\u5C5E\u65E0\u9650\u6D41\u52A8\u6027\u5F15\u64CE" : "DeFi-native infinite liquidity engine"}</Text><Text
    variant="body4"
    color="$accent1"
    fontWeight="600"
    cursor="pointer"
    onPress={() => setShowStories(true)}
  >{
    /* {isZh ? '更多 Ring 的故事 →' : 'More →'} */
  }</Text></Flex>}</Flex>{
    /* Content based on active tab */
  }{activeTab === "ring" ? <MainnetOverview chainId={UniverseChainId.Mainnet} /> : <AggregatorLeaderboard />}</PanelWrapper>{
    /* Ring Stories Modal — portaled to document.body so it overlays the whole page */
  }{showStories && typeof document !== "undefined" && createPortal(
    <ModalOverlay onPress={() => setShowStories(false)}><ModalContent onPress={(e) => e.stopPropagation()}><Flex row alignItems="center" justifyContent="space-between"><Text variant="subheading1" color="$neutral1">{isZh ? "Ring \u7684\u6545\u4E8B" : "Ring Stories"}</Text><Text variant="body2" color="$neutral2" cursor="pointer" onPress={() => setShowStories(false)}>
                  ✕
                </Text></Flex><Flex gap="$spacing12">{RING_STORIES.map((story, idx) => <Flex row alignItems="flex-start" gap="$spacing10" key={idx}><Text variant="body2" fontSize={18}>{story.icon}</Text><Text variant="body3" color="$neutral2" flex={1}>{isZh ? story.text : story.textEn}</Text></Flex>)}</Flex></ModalContent></ModalOverlay>,
    document.body
  )}</ExploreContextProvider>;
});
